import Groq from 'groq-sdk';

// API Key từ Groq (HOÀN TOÀN MIỄN PHÍ)
// Lấy tại: https://console.groq.com/keys
// eslint-disable-next-line no-undef
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY || '';

/**
 * Service tích hợp Groq API - HOÀN TOÀN MIỄN PHÍ & CỰC NHANH
 *
 * Ưu điểm:
 * - Miễn phí 100%
 * - Tốc độ inference cực nhanh (vài trăm tokens/giây)
 * - Models mạnh: Llama 3.1, Mixtral, Gemma
 * - Không cần credit card
 *
 * Cách lấy API key:
 * 1. Truy cập: https://console.groq.com/keys
 * 2. Đăng ký với email (miễn phí)
 * 3. Tạo API key
 * 4. Thêm vào .env: REACT_APP_GROQ_API_KEY=your_key_here
 */
class GroqService {
  constructor() {
    this.isAvailable = false;
    this.client = null;

    // Danh sách models Groq (theo thứ tự ưu tiên)
    // Cập nhật: 2026-02-05 - llama-3.1-70b-versatile đã bị deprecated
    this.modelNames = [
      'llama-3.3-70b-versatile', // Model mới nhất thay thế 3.1-70b
      'llama-3.1-8b-instant', // Nhanh nhất
      'mixtral-8x7b-32768', // Context window dài
      'gemma2-9b-it' // Fallback
    ];
    this.currentModelIndex = 0;

    if (GROQ_API_KEY) {
      try {
        this.client = new Groq({
          apiKey: GROQ_API_KEY,
          dangerouslyAllowBrowser: true // Cho phép gọi từ browser
        });
        this.isAvailable = true;
        console.log('✅ Groq AI đã sẵn sàng - Miễn phí & Cực nhanh!');
        console.log(`📊 API Key: ${GROQ_API_KEY.substring(0, 20)}...`);
      } catch (error) {
        console.warn('⚠️ Khởi tạo Groq thất bại:', error);
        console.log('💡 Kiểm tra lại GROQ_API_KEY trong file .env');
      }
    } else {
      console.warn('⚠️ GROQ_API_KEY chưa được cấu hình. Chatbot sẽ dùng template responses.');
      console.log('💡 Để sử dụng AI:');
      console.log('1. Lấy key miễn phí tại: https://console.groq.com/keys');
      console.log('2. Thêm vào .env: REACT_APP_GROQ_API_KEY=gsk_xxx');
      console.log('3. Restart server');
    }
  }

  /**
   * Gửi tin nhắn đến Groq AI
   */
  async chat(message, conversationHistory = [], systemContext = '') {
    // Fallback nếu không có API key
    if (!GROQ_API_KEY || !this.client || !this.isAvailable) {
      console.warn('🚫 Groq không khả dụng - dùng fallback. Kiểm tra GROQ_API_KEY trong .env');
      return this.getFallbackResponse(message, systemContext);
    }

    console.log('🤖 Đang gọi Groq API...');

    // Xây dựng messages cho Groq
    const messages = [];

    // System message với context
    if (systemContext) {
      messages.push({
        role: 'system',
        content: `Bạn là trợ lý AI của hệ thống GSWM (Quản lý nước). Dựa vào dữ liệu dưới đây, hãy trả lời câu hỏi một cách thông minh, súc tích và hữu ích.

${systemContext}

Hãy phân tích số liệu, so sánh và đưa ra nhận xét có giá trị.

**QUY TẮC FORMAT QUAN TRỌNG:**
1. Trả lời bằng tiếng Việt, súc tích
2. SỬ DỤNG **BOLD** cho:
   • Tất cả SỐ LIỆU: **16,625,179 m³**, **36.6%**, **2,345 khách hàng**
   • GIÁ TRỊ quan trọng: **HP7**, **Trung tâm**, **Đang sử dụng**
   • TÊN riêng: **Pacific Hotel**, **Lê Văn A**
3. Sử dụng *italic* cho labels: *Tổng tiêu thụ:*, *Chi nhánh:*, *Ngày:*
4. Thêm emoji: 📊 💧 🏢 📈 📍 🔍

**LƯU Ý QUAN TRỌNG VỀ 2 LOẠI DỮ LIỆU TIÊU THỤ:**
⚡ **LOẠI 1: TIÊU THỤ THEO NGÀY** (Branch consumption by day)
   - Dữ liệu: Tiêu thụ HÀNG NGÀY của các chi nhánh
   - Thời gian: Theo ĐỊA ĐIỂM (ngày cụ thể: 05/02/2026)
   - Đơn vị: Chi nhánh
   - Ví dụ câu hỏi: "Tiêu thụ hôm nay của chi nhánh HP7"

⚡ **LOẠI 2: TIÊU THỤ THEO KỲ CƯỚC** (Monthly consumption by billing period)
   - Dữ liệu: Tiêu thụ theo KỲ CƯỚC/THÁNG của từng khách hàng
   - Thời gian: Theo KỲ HÓA ĐƠN (tháng 02/2026)
   - Đơn vị: Khách hàng cá nhân
   - Bao gồm: Chỉ số cũ, chỉ số mới, tiêu thụ, trạng thái
   - Ví dụ câu hỏi: "Tiêu thụ tháng này của khách hàng Pacific"

=> KHI TRẢ LỜI: Luôn làm rõ người dùng đang xem loại dữ liệu nào!

**CẢNH BÁO THIẾT BỊ:**
⚠️ **TRA CỨU CẢNH BÁO**
   - Dữ liệu: Cảnh báo thiết bị đo nước của khách hàng
   - Yêu cầu: BẮT BUỘC phải có từ khóa "cảnh báo" + MÃ KHÁCH HÀNG (6-10 chữ số)
   - Format đúng: "cảnh báo 6816947" hoặc "tra cứu cảnh báo 6816947"
   - Format SAI: chỉ gõ "6816947" (thiếu từ khóa "cảnh báo")
   - Trạng thái: 🔴 Chưa xử lý (status=0), 🟢 Đã xử lý (status=1)
   - Thông tin: Tên cảnh báo, số sêri thiết bị, ngày xảy ra, chi nhánh
   - Nếu thiếu từ khóa "cảnh báo": Hướng dẫn user gõ lại đúng format

5. VÍ DỤ ĐÚNG:
   
   📊 *Tổng tiêu thụ:* **45,447,936 m³**
   
   🏢 *Top 3 chi nhánh:*
   1. **HP7**: **16,625,179 m³** (**36.6%**)
   2. **Trung tâm**: **14,536,678 m³** (**32.0%**)
   3. **HP8**: **4,915,889 m³** (**10.8%**)
   
   💡 *Nhận xét:* **HP7** và **Trung tâm** chiếm **68.6%** tổng tiêu thụ.

QUAN TRỌNG: Phải bôi đậm TẤT CẢ số liệu, phần trăm, tên riêng!`
      });
    } else {
      messages.push({
        role: 'system',
        content: `Bạn là trợ lý AI thông minh của hệ thống GSWM.

**QUY TẮC FORMAT:**
• Trả lời bằng tiếng Việt, ngắn gọn
• BẮT BUỘC dùng **bold** cho MỌI số liệu, giá trị, tên riêng
• **Bold**: CHỈ cho số liệu và giá trị
• *Italic*: Cho labels và tiêu đề
• Dùng emoji phù hợp
• TRÁNH quá nhiều **bold**`
      });
    }

    // Thêm lịch sử conversation
    conversationHistory.slice(-6).forEach((msg) => {
      messages.push({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      });
    });

    // Thêm câu hỏi hiện tại
    messages.push({
      role: 'user',
      content: message
    });

    // Thử gọi API với retry logic
    while (this.currentModelIndex < this.modelNames.length) {
      try {
        const modelName = this.modelNames[this.currentModelIndex];

        const completion = await this.client.chat.completions.create({
          messages: messages,
          model: modelName,
          temperature: 0.7,
          max_tokens: 1024,
          top_p: 1,
          stream: false
        });

        const responseText = completion.choices[0]?.message?.content || '';

        if (responseText) {
          console.log(`✅ Groq thành công với model: ${modelName}`);
          return responseText;
        }
      } catch (error) {
        const errorMsg = error.message || '';
        console.warn(`⚠️ Groq model ${this.modelNames[this.currentModelIndex]} lỗi:`, errorMsg);

        // Thử model tiếp theo
        this.currentModelIndex++;

        if (this.currentModelIndex >= this.modelNames.length) {
          console.error('❌ Đã thử hết tất cả Groq models');
          break;
        }

        console.log('🔄 Thử model tiếp theo...');
      }
    }

    // Nếu tất cả models đều lỗi, dùng fallback
    this.isAvailable = false;
    return this.getFallbackResponse(message, systemContext);
  }

  /**
   * Response fallback khi API không hoạt động
   */
  getFallbackResponse(message, systemContext) {
    const lowerMessage = message.toLowerCase();

    console.log('⚠️ Đang dùng Fallback Response (không có AI)');

    // Nếu có systemContext (dữ liệu từ API backend), hiển thị dữ liệu
    if (systemContext && systemContext.trim().length > 0) {
      let response = '';

      if (lowerMessage.includes('chi nhánh') || lowerMessage.includes('branch')) {
        response = '📊 **Kết quả tra cứu tiêu thụ theo chi nhánh:**\n\n';
      } else if (lowerMessage.includes('tiêu thụ')) {
        response = '📊 **Dữ liệu tiêu thụ:**\n\n';
      } else if (lowerMessage.includes('thống kê')) {
        response = '📊 **Thống kê hệ thống:**\n\n';
      } else if (lowerMessage.includes('khách hàng')) {
        response = '📊 **Thông tin khách hàng:**\n\n';
      } else {
        response = '📊 **Kết quả tra cứu:**\n\n';
      }

      response += systemContext;

      response += '\n\n⚠️ _Lưu ý: Đang dùng template response (AI chưa kích hoạt)_';
      response += '\n💡 _Để có câu trả lời thông minh hơn với AI:_';
      response += '\n1. Lấy key tại: https://console.groq.com/keys';
      response += '\n2. Thêm vào .env: REACT_APP_GROQ_API_KEY=gsk_xxx';

      return response;
    }

    // Response mặc định
    return `Xin chào! Tôi là trợ lý của hệ thống GSWM.

Tôi có thể giúp bạn về:
• Dữ liệu tiêu thụ nước
• Thống kê khách hàng
• Báo cáo theo chi nhánh
• Giám sát tiêu thụ

💡 **Để sử dụng AI thông minh (Miễn phí):**
1. Truy cập: https://console.groq.com/keys
2. Đăng ký miễn phí (không cần thẻ)
3. Tạo API key
4. Thêm vào .env: \`REACT_APP_GROQ_API_KEY=your_key\`
5. Reload trang

Bạn muốn hỏi gì?`;
  }
}

export const groqService = new GroqService();
